/**
 * URL handling shared by the import panel, the browse route and the API.
 *
 * Here rather than in `apps/web` because apps/api needs `isLocalTarget` too —
 * the instant-audit route has to refuse a localhost URL before it provisions a
 * worker that could never reach one, and a second hand-written copy of that
 * rule is exactly the drift this package exists to prevent.
 *
 * Runtime values, so it is exported twice: from `entry.ts` for apps/api, which
 * resolves as `node` against the CJS dist, and as the `./target-url` source
 * subpath for apps/web, which cannot statically analyse that dist for named
 * exports. Same arrangement as the credit constants, and for the same reason.
 *
 * The worker normalizes and net-guards URLs again on its side (net-guard.ts) —
 * this is purely so the user finds out about a typo before a browser instance
 * is provisioned for it, not a security boundary.
 */

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Hostnames only: letters, digits, dots and hyphens, or a bracketed IPv6
 * literal.
 *
 * Needed because `new URL()` is not the validator it looks like, and browsers
 * disagree with each other: `new URL('https://not a url')` throws in Node and
 * jsdom, but Chrome happily returns one with the host percent-encoded to
 * `not%20a%20url`. Without this check that typo passes validation in the only
 * environment that matters, and the user is sent to a boot screen for a site
 * that cannot exist.
 */
const VALID_HOST =
  /^(\[[0-9a-f:.]+\]|[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*)$/i;

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]']);

/** Host part of a scheme-less input: "localhost:5173/x" → "localhost". */
function isLocalHost(input: string): boolean {
  const host = input.split('/')[0]?.split(':')[0] ?? '';
  return LOCAL_HOSTS.has(host) || host.endsWith('.local');
}

/**
 * Whether this is a page only the user's own machine can reach.
 *
 * The browser worker runs in Cloud Run, so these are exactly the URLs it can
 * never load — the import screen sends them down the Chrome-extension path
 * instead of provisioning an instance that would fail on connect.
 */
export function isLocalTarget(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return (
      LOCAL_HOSTS.has(host) ||
      LOCAL_HOSTS.has(`[${host}]`) ||
      host.endsWith('.local')
    );
  } catch {
    return false;
  }
}

/** Turns what a user typed into an absolute http(s) URL, or null. */
export function normalizeTargetUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // "stripe.com" has no protocol; the URL constructor rejects it outright.
  // Local targets default to http, not https: a dev server on :5173 almost
  // never has a certificate, and "localhost" is advertised as supported on the
  // import screen, so silently sending it to https would break the one target
  // every user tries first.
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed)
    ? trimmed
    : `${isLocalHost(trimmed) ? 'http' : 'https'}://${trimmed}`;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) return null;
  if (!VALID_HOST.test(parsed.hostname)) return null;

  return parsed.toString();
}

/** "https://stripe.com/pricing?x=1" → "stripe.com/pricing". For the URL bar. */
export function displayUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname === '/' ? '' : parsed.pathname;
    return `${parsed.host}${path}${parsed.search}`;
  } catch {
    return url;
  }
}

/** "https://stripe.com/pricing" → "stripe.com". For status copy. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
